import { useState, useEffect, useRef } from "react";

// Vercel 배포된 프록시 API 주소
// - 웹: 같은 오리진이라 상대 경로도 동작하지만, 네이티브 webview(Capacitor)는 file:// 또는 localhost이므로 절대 URL 필요
const API_URL = "https://crewpick.vercel.app/api/naver-search";

function stripHtml(s) {
  return (s || "").replace(/<[^>]*>/g, "");
}

export default function AddressSearchModal({ open, onClose, onSelect, initialQuery = "" }) {
  const [query, setQuery] = useState(initialQuery);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery(initialQuery);
      setItems([]);
      setError("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, initialQuery]);

  const search = async () => {
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setError("");
    setItems([]);
    try {
      const res = await fetch(`${API_URL}?query=${encodeURIComponent(q)}`);
      if (!res.ok) {
        setError("검색에 실패했어요. 잠시 후 다시 시도해주세요.");
        return;
      }
      const data = await res.json();
      const list = (data.items || []).map((it) => ({
        title: stripHtml(it.title),
        category: it.category || "",
        roadAddress: it.roadAddress || "",
        address: it.address || "",
        mapx: it.mapx ? Number(it.mapx) : null,  // WGS84 경도 × 10^7
        mapy: it.mapy ? Number(it.mapy) : null,  // WGS84 위도 × 10^7
      }));
      if (list.length === 0) setError("검색 결과가 없어요.");
      setItems(list);
    } catch (e) {
      setError("네트워크 오류가 발생했어요.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div style={s.backdrop} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <span style={s.title}>장소 검색</span>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={s.searchRow}>
          <input
            ref={inputRef}
            style={s.input}
            placeholder="장소/체육시설 이름 (예: 강서테니스장)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
          />
          <button style={s.searchBtn} onClick={search} disabled={loading}>
            {loading ? "..." : "검색"}
          </button>
        </div>

        <div style={s.resultList}>
          {error && <div style={s.errorMsg}>{error}</div>}
          {items.map((it, i) => (
            <div
              key={i}
              style={s.resultItem}
              onClick={() => {
                onSelect({
                  name: it.title,
                  roadAddress: it.roadAddress,
                  address: it.address,
                  category: it.category,
                  mapx: it.mapx,
                  mapy: it.mapy,
                });
                onClose();
              }}
            >
              <div style={s.resultTitle}>{it.title}</div>
              {it.category && <div style={s.resultCategory}>{it.category}</div>}
              {(it.roadAddress || it.address) && (
                <div style={s.resultAddr}>{it.roadAddress || it.address}</div>
              )}
            </div>
          ))}
          {!loading && !error && items.length === 0 && (
            <div style={s.placeholder}>검색어를 입력하고 검색 버튼을 눌러주세요</div>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  backdrop: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 400, backdropFilter: "blur(4px)", padding: 16,
  },
  modal: {
    width: "100%", maxWidth: 400, maxHeight: "75vh",
    background: "#fff", borderRadius: 16,
    display: "flex", flexDirection: "column", overflow: "hidden",
    boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "14px 18px", borderBottom: "1px solid #edf0f3",
    background: "#fafafa",
  },
  title: { fontSize: 15, fontWeight: 700, color: "#111827" },
  closeBtn: {
    background: "none", border: "none", fontSize: 20, color: "#9ca3af",
    cursor: "pointer", padding: "4px 8px", lineHeight: 1,
  },
  searchRow: {
    display: "flex", gap: 8, padding: "12px 16px",
    borderBottom: "1px solid #edf0f3", background: "#fff",
  },
  input: {
    flex: 1, padding: "10px 14px", border: "1.5px solid #e5e7eb",
    borderRadius: 10, fontSize: 14, outline: "none", fontFamily: "inherit",
    minWidth: 0,
  },
  searchBtn: {
    padding: "10px 16px", background: "#E24B4A", color: "#fff",
    border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700,
    cursor: "pointer", minWidth: 56, flexShrink: 0,
  },
  resultList: {
    flex: 1, overflowY: "auto", padding: "6px 0",
    WebkitOverflowScrolling: "touch",
  },
  resultItem: {
    padding: "12px 18px", borderBottom: "1px solid #f3f4f6",
    cursor: "pointer", transition: "background 0.1s",
  },
  resultTitle: {
    fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 4,
    lineHeight: 1.3,
  },
  resultCategory: {
    fontSize: 11, color: "#9ca3af", marginBottom: 3,
    lineHeight: 1.3,
  },
  resultAddr: {
    fontSize: 12, color: "#6b7280", lineHeight: 1.4,
    wordBreak: "keep-all",
  },
  errorMsg: {
    padding: "24px 20px", textAlign: "center",
    color: "#dc2626", fontSize: 13,
  },
  placeholder: {
    padding: "48px 20px", textAlign: "center",
    color: "#9ca3af", fontSize: 13, lineHeight: 1.6,
  },
};
